import {
  SupportedBibleTranslation,
  CLASSROOM_HELP_CENTER_URL,
} from "~/constants";

import { isMobileNative } from "./helpers";
import { logger } from "./logging";
import { getFullUrl } from "./url";
import { getBibleReaderLink, sanitizeReference } from "./scripture";

export type PageContentType = "class" | "module" | "notebook" | "session";

export type PageStencil = {
  /** The data required to construct the stencil. */
  propsJSON: string;
  type: PageContentType;
};

export type Action =
  | {
      auth: AuthAction;
    }
  | {
      error: ErrorAction;
    }
  | {
      navigation: NavigationAction;
    }
  | {
      ui: UIAction;
    };

export type AuthAction = AuthAccessTokenAction;

export type AuthAccessTokenAction = {
  accessToken: Record<string, never>;
};

export type AuthAccessTokenActionResult = {
  token?: string;
};

export type ErrorAction = {
  unhandled?: {
    fileName?: string;
    message?: string;
    stack?: string;
  };
};

export type NavigationActionType =
  | "dismiss"
  | "pop"
  | "present"
  | "presentFullScreen"
  | "presentGiving"
  | "presentLink"
  | "presentLogin"
  | "presentScripture"
  | "push";

export type NavigationDismissAction = {
  dismiss: {
    all: boolean;
  };
};

export type NavigationPopAction = {
  pop: Record<string, never>;
};

export type NavigationPresentAction = {
  present: {
    stencil?: PageStencil;
    url: string;
  };
};

export type NavigationGivingAction = {
  presentGiving: Record<string, never>;
};

export type NavigationPresentFullScreenAction = {
  presentFullScreen: {
    stencil?: PageStencil;
    url: string;
  };
};

export type NavigationPresentLinkAction = {
  presentLink: {
    url: string;
  };
};

export type NavigationPresentLoginAction = {
  presentLogin: {
    description?: string;
    title?: string;
  };
};

export type MailComposerData = {
  body: string;
  subject: string;
  to: string;
};

export type NavigationPresentMailComposerAction = {
  presentMailComposer: MailComposerData;
};

export type NavigationPresentMailComposerActionResponse = {
  didPresent?: boolean;
};

export type NavigationPresentScriptureReferenceAction = {
  presentScriptureReference: {
    reference: string;
    translation: number;
  };
};

export type NavigationPresentVideoAction = {
  presentVideo: {
    id: number;
    paths: {
      m3u8?: string;
      mp4?: string;
    };
    title: string;
  };
};

export type NavigationPushAction = {
  push: {
    stencil?: PageStencil;
    url: string;
  };
};

export type NavigationAction =
  | NavigationDismissAction
  | NavigationPopAction
  | NavigationPresentAction
  | NavigationPresentFullScreenAction
  | NavigationGivingAction
  | NavigationPresentLinkAction
  | NavigationPresentLoginAction
  | NavigationPresentMailComposerAction
  | NavigationPresentScriptureReferenceAction
  | NavigationPresentVideoAction
  | NavigationPushAction;

export type UIHapticAction = {
  haptic: {
    type: "notificationError" | "notificationSuccess";
  };
};

export type UIOrientationLockType = "portrait" | "landscape" | false;

export type UIOrientationLockAction = {
  requestOrientationLock: {
    type: UIOrientationLockType;
  };
};

export type UIOrientationToggleAction = {
  requestOrientationToggle: Record<string, never>;
};

export type UIReadyAction = {
  ready: Record<string, never>;
};

export type UIScreenTimerEvent = {
  setScreenTimerDisabled: {
    disabled: boolean;
  };
};

export type UIScrollAction = {
  setScrollingEnabled: {
    enabled: boolean;
  };
};

export type UIInteractiveDismissAction = {
  setInteractiveDismiss: {
    disabled: boolean;
  };
};

export type UITabBarAction = {
  setTabBarVisible: {
    animated: boolean;
    visible: boolean;
  };
};

export type UIShareAction = {
  share: {
    rect: [[number, number], [number, number]];
    url: string;
  };
};

export type UIStatusBarStyle = "light" | "dark";

export type UIStatusBarAction = {
  setStatusBarStyle: {
    style: "light" | "dark";
  };
};

export type UIAction =
  | UIHapticAction
  | UIInteractiveDismissAction
  | UIOrientationLockAction
  | UIOrientationToggleAction
  | UIReadyAction
  | UIScreenTimerEvent
  | UIScrollAction
  | UIShareAction
  | UIStatusBarAction
  | UITabBarAction;

export type HostMessage = Action;

export enum TranslationValue {
  NASB = 1,
  AMP = 2,
  NIV = 3,
  NIRV = 5,
  NLT = 4,
}

export const TranslationMap = new Map(
  Object.entries({
    [SupportedBibleTranslation.NASB]: TranslationValue.NASB,
    [SupportedBibleTranslation.AMP]: TranslationValue.AMP,
    [SupportedBibleTranslation.NIV]: TranslationValue.NIV,
    [SupportedBibleTranslation.NIRV]: TranslationValue.NIRV,
    [SupportedBibleTranslation.NLT]: TranslationValue.NLT,
  }),
) as Map<keyof typeof SupportedBibleTranslation, TranslationValue>;

export const AUTH_STATE_CHANGED_EVENT = "classroom:authStateChanged";
export const COMPLETION_EVENT = "classroom:completion";
export const PAGE_SHOULD_SAVE_STATE_EVENT = "classroom:pageShouldSaveState";
export const STENCIL_EVENT = "classroom:stencil";
export const SHEET_EVENT = "classroom:sheet";

let uid = 0;

/**
 * Determines if a message should be ignore based on
 * platform heuristics.
 */
export function shouldIgnoreMessage(message: HostMessage) {
  // Ignore setTabBarVisible on views without a tab bar.
  if (
    // @ts-expect-error
    message.ui?.setTabBarVisible &&
    window.BibleProjectApp?.navigationType &&
    ["present", "presentFullScreen"].includes(
      window.BibleProjectApp.navigationType,
    )
  ) {
    return true;
  }

  return false;
}

/**
 * Sends a message payload to the host application,
 * specifically the mobile app WebView.
 */
export function sendMessageToHost(
  message: HostMessage,
  options: {
    id?: string;
    // A message that will be run after the primary message
    // has completed This is needed because events such as
    // dismiss/pop prevent sendMessageToHostWithCompletion
    // from being used because the WebView is deallocated
    completionMessage?: HostMessage;
  } = {},
  // Determines if the message should be sent. Passed as an
  // argument so that the striped-down bundling used for
  // stencils works without bundling addtional unneeded
  // code. This keeps the function "pure".
  predicate: (message: HostMessage) => boolean = (message) =>
    !shouldIgnoreMessage(message),
) {
  if (!predicate?.(message)) return;

  function createHostMessage(
    message: HostMessage,
    options: {
      id?: string;
      completionMessage?: HostMessage;
    } = {},
  ): Record<string, any> {
    return Object.keys(message).reduce(
      (message_, key) => {
        return {
          ...message_,
          [key]: {
            action: {
              ...message_[key],
            },
            completionAction: options.completionMessage
              ? createHostMessage(options.completionMessage)
              : undefined,
            id: options.id,
          },
        };
      },
      message as Record<string, any>,
    );
  }

  const hostMessage = createHostMessage(message, options);

  // iOS
  if (window.webkit?.messageHandlers?.BibleProjectApp) {
    window.webkit.messageHandlers.BibleProjectApp.postMessage(
      JSON.stringify(hostMessage),
    );
    return;
  }

  // Android
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView?.postMessage(JSON.stringify(hostMessage));
    return;
  }

  // Fallback
  document.dispatchEvent(
    new CustomEvent("classroom:message", { detail: hostMessage }),
  );

  logger.debug("classroom:message", hostMessage);
}

/**
 * Sends a message payload to the host application and
 * awaits a message back.
 * @see sendMessageToHost
 */
export function sendMessageToHostWithCompletion<T = void>(
  message: HostMessage,
): Promise<T> {
  return new Promise((resolve) => {
    const id = `${COMPLETION_EVENT}:${uid++}`;
    document.addEventListener(
      id,
      (event: CustomEventInit) => {
        resolve(event.detail);
      },
      { once: true },
    );
    sendMessageToHost(message, { id });
  });
}

/**
 * Creates the payload for the share message. Includes
 * geometry for host platforms that require it (e.g. iPad).
 */
export function createShareMessage(
  url: string,
  target: EventTarget | null,
): Action {
  let rect: [[number, number], [number, number]] = [
    [0, 0],
    [0, 0],
  ];
  if (target instanceof HTMLElement) {
    const { x, y, width, height } = target.getBoundingClientRect();
    rect = [
      [x, y],
      [width, height],
    ];
  }
  return {
    ui: {
      share: { rect, url },
    },
  };
}

export interface IssueInfo {
  url: string;
  webVersion: string;
  userAgent: string;
  session?: number;
  timestamp?: number;
  slideId?: string;
}

/**
 * Creates MailComposerData for classroom feedback.
 */
export function createFeedbackMailComposerData(
  info: IssueInfo,
): MailComposerData {
  return {
    body: `
        Please share details about the bug/issue and include any relevant screenshots here:

        ---
        URL: ${info.url}
        User Agent: ${info.userAgent}
        Classroom version: ${info.webVersion}
        ${
          info.session || info.timestamp || info.slideId
            ? `Session: ${info.session}
                 Timestamp: ${info.timestamp}
                 SlideId: ${info.slideId}`
            : ""
        }
      `
      .trim()
      .replaceAll(/\n[ \t]+/g, "\n"),
    subject: "BibleProject Classroom Feedback",
    to: "classroom.support@bibleproject.com",
  };
}

/**
 * Creates mailTo: link href for classroom feedback.
 */
export function createIssueMailToHref(info: IssueInfo) {
  const composerData = createFeedbackMailComposerData(info);
  return `mailTo:${composerData.to}?subject=${encodeURIComponent(composerData.subject)}&body=${encodeURIComponent(composerData.body)}"`;
}

/**
 * Creates the payload for the "Report an issue" event.
 */
export function createIssueMessage(info: IssueInfo): Action {
  const action: NavigationPresentMailComposerAction = {
    presentMailComposer: createFeedbackMailComposerData(info),
  };

  return {
    navigation: action,
  };
}

/**
 * Creates the payload for request orientation toggle
 * message.
 */
export function createRequestOrientationToggleMessage() {
  const action: UIOrientationToggleAction = {
    requestOrientationToggle: {},
  };
  return { ui: action };
}

/**
 * Creates the payload for loading a URL in a "presenation"
 * view.
 */
export function createPresentLinkMessage(url: string) {
  const action: NavigationPresentLinkAction = {
    presentLink: { url },
  };
  return { navigation: action };
}

/**
 * Creates the payload displaying a scripture reference.
 */
export function createPresentScriptureReferenceMessage(
  reference: string,
  translation: SupportedBibleTranslation = SupportedBibleTranslation.NASB,
) {
  const action: NavigationPresentScriptureReferenceAction = {
    presentScriptureReference: {
      reference: sanitizeReference(reference),
      translation: TranslationMap.get(translation) ?? TranslationValue.NASB,
    },
  };
  return { navigation: action };
}

/**
 * Creates the payload for request orientation lock message.
 */
export function createRequestOrientationLockMessage(
  type: UIOrientationLockType,
) {
  const action: UIOrientationLockAction = {
    requestOrientationLock: { type },
  };
  return { ui: action };
}

/**
 * Requests orientation toggle based on the host platform.
 */
export function requestOrientationToggle() {
  if (isMobileNative()) {
    sendMessageToHost(createRequestOrientationToggleMessage());
  } else {
    logger.debug("Request orientation toggle");
  }
}

export function presentLink(url: string) {
  if (isMobileNative()) {
    sendMessageToHost(createPresentLinkMessage(url));
  } else {
    logger.debug("Present link", { url });
  }
}

export function presentScriptureReference(
  reference: string,
  translation: SupportedBibleTranslation = SupportedBibleTranslation.NASB,
) {
  if (isMobileNative()) {
    sendMessageToHost(
      createPresentScriptureReferenceMessage(reference, translation),
    );
  } else {
    window.open(getBibleReaderLink(reference, translation), "_blank");
  }
}

/**
 * Present the give experience modal.
 */
export function presentGive() {
  if (isMobileNative()) {
    sendMessageToHost({
      navigation: {
        presentGiving: {},
      },
    });
  } else {
    globalThis.open("https://bibleproject.com/give/classroom", "_blank");
  }
}

export function requestOrientationLock(type: UIOrientationLockType) {
  if (isMobileNative()) {
    sendMessageToHost(createRequestOrientationLockMessage(type));
  } else {
    logger.debug("Request orientation lock", { type });
  }
}

/**
 * Send message issue, with fallback to webpage.
 */
export async function presentReportIssue(info: IssueInfo) {
  if (isMobileNative()) {
    const composerSucceded =
      await sendMessageToHostWithCompletion<NavigationPresentMailComposerActionResponse>(
        createIssueMessage(info),
      ).then((result) => result.didPresent);

    // TODO: [CLASS-865] Localize help issue.
    if (!composerSucceded) {
      sendMessageToHost(createPresentLinkMessage(CLASSROOM_HELP_CENTER_URL));
    }
  } else {
    globalThis.open(CLASSROOM_HELP_CENTER_URL, "_blank");
  }
}

/**
 * Sets the native status bar style.
 */
export function setPageStatusBarStyle(style: UIStatusBarStyle) {
  const uiAction: UIStatusBarAction = {
    setStatusBarStyle: { style },
  };
  sendMessageToHost({ ui: uiAction });
}

/**
 * Opens a share modal.
 */
export const sharePage = (path: string, target: EventTarget | null) => {
  const url = getFullUrl(path);
  sendMessageToHost(createShareMessage(url, target));
};
